import cx from 'classnames';
import {filter, first, sortBy} from 'lodash';
import {useMemo, Fragment, useState, useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, Timer, Tooltip} from 'components';
import {useObservable, useUserSession} from 'hooks';
import {SessionStatus} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {onlineSessionsStore} from 'services';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE;

export const SessionQueue: React.FC = () => {
    const {t} = useTranslation();
    const {settings: {session: {queue: queueSettings}}} = useUserSession();

    const {sessions} = useObservable(onlineSessionsStore);
    const {count, latest} = useMemo(() => {
        const callingSessions = filter(sessions, (session) => session.status === SessionStatus.Calling);
        const firstClientWaitingSession = first(sortBy(callingSessions, (session) => +new Date(session.startDate)));
        return {count: callingSessions.length, latest: firstClientWaitingSession?.startDate};
    }, [sessions]);

    const [latestDate, setLatestDate] = useState<Date>();
    const waitingInSeconds = useMemo(() => latestDate ? (+Date.now() - +latestDate) / 1000 : 0, [latestDate]);
    const {isWarning, isCritical} = useMemo(() => {
        if (count > queueSettings.critical.count || waitingInSeconds > queueSettings.critical.period) {
            return {isCritical: true};
        } else if (count > queueSettings.warning.count || waitingInSeconds > queueSettings.warning.period) {
            return {isWarning: true};
        }
        return {};
    }, [count, waitingInSeconds, queueSettings]);

    const refreshLatestDate = useCallback(() => {
        setLatestDate(latest ? new Date(latest) : null);
    }, [latest]);

    const onTimerChange = useCallback(() => {
        refreshLatestDate();
    }, [refreshLatestDate]);

    useEffect(() => {
        refreshLatestDate();
    }, [refreshLatestDate]);

    return (
        <Tooltip header={t(T_KEYS.LABELS.SESSION_QUEUE)}>
            <div className={cx('tw-flex tw-items-center', {'tw-text-warning': isWarning, 'tw-text-alert': isCritical})}>
                <Icon name='queue' color={isCritical ? 'alert' : isWarning ? 'warning' : undefined} className='tw-mr-1' />
                {count}
                {count > 0 && (
                    <Fragment>
                        {' ('}
                        <Timer
                            config={{optionalHours: true, minutes: true, seconds: true}}
                            startDate={latestDate}
                            onDiffChange={onTimerChange}
                        />
                        {')'}
                    </Fragment>
                )}
            </div>
        </Tooltip>
    );
};
